import React, { useCallback, useState } from 'react'
import { Controller, Control, useFormContext } from 'react-hook-form'
import makeStyles from '@mui/styles/makeStyles'
import { Button, Grid, TextField } from '@mui/material'
import { ACL } from '@shared/components/acl.component'
import {
  Audit,
  AuditRequirement,
  AuditRequirementEnhanced,
  Roles,
  Template,
} from '@shared/types'
import { useAuditQuery } from '@shared/hooks/use-audit-query.hook'
import { Autocomplete } from '@mui/material'
import { orderBy } from 'lodash'
import { FormTemplates } from '@modules/requirement/requirement-form/form-templates.component'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import { useRouter } from '@shared/hooks/use-router'
import { registerInputRefFactory } from '@modules/admin-generic/admin-generic.utils'
import { NO_OPTIONS_PLACEHOLDER } from '@shared/consts'

type RouteQuery = {
  companyID: string
  auditID: string
  auditRequirementID: string
}
type RouteLocation = {
  requirement: AuditRequirementEnhanced
  audit: Audit
  isPreviewMode: boolean
}

export type RequirementDetailsFormData = {
  saveAsTemplate: boolean
  saveAsTemplateEnhanced: boolean
  fixDate: AuditRequirement['fixDate']
  summary: AuditRequirement['summary']
  auditorAssessment: AuditRequirement['auditorAssessment']
  clientComment?: string
  connectedAuditRequirements: AuditRequirement['connectedAuditRequirements']
  connectedAspects: AuditRequirement['connectedAspects']
  connectedDecisions: AuditRequirement['connectedDecisions']
  connectedDocuments: AuditRequirement['connectedDocuments']
}

type Props = {
  auditID: number
  statusID: number
  requirementBaseID: number
  formControl: Control<RequirementDetailsFormData>
  isAuditorAssessmentEmpty: boolean
  onTakePhoto: () => void
  onUploadPhoto: () => void
  isClient: boolean
  isClientOrClientEditor: boolean
  setSelectedTemplate: (value: Template) => void
  companyID: number
}

export function FormStatusDetails({
  auditID,
  statusID,
  requirementBaseID,
  formControl,
  isAuditorAssessmentEmpty,
  onTakePhoto,
  onUploadPhoto,
  isClient,
  isClientOrClientEditor,
  setSelectedTemplate,
  companyID,
}: Props) {
  const classes = useStyles()
  const { audit, isAuditLoading } = useAuditQuery(auditID)
  const { location } = useRouter<RouteQuery>()
  const [showTemplates, setShowTemplates] = useState(false)

  const { register, setValue } = useFormContext()
  const registerInputRef = registerInputRefFactory(register)

  const onShowTemplatesClick = useCallback(() => {
    setShowTemplates(!showTemplates)
  }, [showTemplates])

  const onSetTemplate = useCallback(
    (templateText: any) => {
      setValue('auditorAssessment', templateText)
      setShowTemplates(false)
    },
    [setShowTemplates, setValue]
  )

  return location.state.isPreviewMode ? null : (
    <>
      <ACL
        allowedRoles={[
          Roles.ClientEditor,
          Roles.ClientAdministrator,
          Roles.Admin,
        ]}
      >
        <>
          <TextField
            {...registerInputRef('clientComment')}
            disabled={isClient}
            label={
              isClientOrClientEditor
                ? 'Mój komentarz (klient)'
                : 'Komentarz klienta'
            }
            fullWidth
            multiline
            rows={4}
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
        </>
      </ACL>
      {!isAuditLoading ? (
        <ACL allowedRoles={[Roles.Admin, Roles.Auditor]}>
          <>
            <TextField
              {...registerInputRef('auditorAssessment')}
              label="Realizacja wymagania"
              fullWidth
              multiline
              maxRows={20}
              rows={6}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
            />
            <Grid container>
              <FormTemplates
                showTemplates={showTemplates}
                requirementBaseID={requirementBaseID}
                onSetTemplate={onSetTemplate}
                statusID={statusID}
                onShowTemplatesClick={onShowTemplatesClick}
                saveDisabled={isAuditorAssessmentEmpty}
                controlName="saveAsTemplate"
                setSelectedTemplate={setSelectedTemplate}
              >
                <>
                  <Button
                    variant="text"
                    color="primary"
                    startIcon={<PhotoCameraIcon />}
                    onClick={onTakePhoto}
                  >
                    Zrób zdjęcie
                  </Button>
                  <Button
                    variant="text"
                    color="primary"
                    startIcon={<PhotoCameraIcon />}
                    onClick={onUploadPhoto}
                  >
                    Dodaj zdjęcie z dysku
                  </Button>
                </>
              </FormTemplates>
            </Grid>
            <Controller
              defaultValue={[]}
              control={formControl}
              name="connectedAuditRequirements"
              render={(formControllerProps) => (
                <Autocomplete
                  {...formControllerProps}
                  value={audit?.auditRequirements.filter((req) =>
                    formControllerProps.field.value.includes(
                      req.auditRequirementID
                    )
                  )}
                  multiple
                  className={classes.connectedRequirementsAutocomplete}
                  options={orderBy(audit?.auditRequirements, 'name')}
                  getOptionLabel={(option) => option.name}
                  fullWidth
                  getOptionKey={(option) => option.auditRequirementID}
                  filterSelectedOptions
                  onChange={(event, values) => {
                    const requirementIds = values.map(
                      (i) => i.auditRequirementID ?? i
                    )
                    const dedupedValues = requirementIds.filter(
                      (value, index) => requirementIds.indexOf(value) === index
                    )
                    formControllerProps.field.onChange(dedupedValues)
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Powiązane wymagania" />
                  )}
                  noOptionsText={NO_OPTIONS_PLACEHOLDER}
                />
              )}
            />
          </>
        </ACL>
      ) : null}
    </>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    actionButtonsContainer: {
      alignItems: 'center',
      display: 'flex',
    },
    connectedRequirementsAutocomplete: {
      marginTop: theme.spacing(2),
    },
  }
})
